
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import React from "react";
import Page from "@/src/components/Page";
import DocShell from "@/src/layouts/DocShell";
import DocNavItem from "@/src/components/docs/DocNavItem";
import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  List,
  ListIcon,
  ListItem,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorMode,
} from "@chakra-ui/react";
import H1 from "@/src/components/docs/H1";
import { IoRemoveOutline } from "react-icons/io5";
import H2 from "@/src/components/docs/H2";
import { MdCheckCircle } from "react-icons/md";
import CustomLink from "@/src/components/CustomLink";
import useTranslation from "next-translate/useTranslation";

const LearnMore = () => {
  let { t } = useTranslation();
  const { colorMode } = useColorMode();

  return (
    <DocShell
      aside={
        <>
          <DocNavItem icon={IoRemoveOutline} to={"overview"}>
            {" "}
            {t("learn-more:learn_more.overview.title")}
            {/*Overview*/}
          </DocNavItem>
          <DocNavItem icon={IoRemoveOutline} to={"how-it-works"}>
            {t("learn-more:learn_more.howWork.title")}
            {/*How it works ?*/}
          </DocNavItem>
          <DocNavItem icon={IoRemoveOutline} to={"add-stacks"}>
            {t("learn-more:learn_more.howAdd.title")}
            {/*How to add stacks ?*/}
          </DocNavItem>
          <DocNavItem icon={IoRemoveOutline} to={"next"}>
            {t("learn-more:learn_more.whatNext.title")}
            {/*"What's next"*/}
          </DocNavItem>
          {/*<DocNavItem icon={IoRemoveOutline}>Integrations</DocNavItem>*/}
          {/*<DocNavItem icon={IoRemoveOutline}>Changelog</DocNavItem>*/}
        </>
      }
    >
      <Box
        bg={`${colorMode === "light" ? "white" : "#021b17"}`}
        as={"article"}
        py={4}
        px={{ base: 2, "2xl": 8, "3xl": 12 }}
      >
        <H1
          color={colorMode === "light" ? "inherit" : "brand.600"}
          id={"overview"}
        >
          {t("learn-more:learn_more.overview.title")}
          {/*Overview*/}
        </H1>
        <Box
          ml={{ base: "9px", "2xl": "18px" }}
          fontSize={{ base: "md", "2xl": "md", "5xl": "18px" }}
          py={5}
          color={colorMode === "light" ? "gray.700" : "white"}
        >
          <H2>{t("learn-more:learn_more.overview.subtitle1")}</H2>
          <Text as={"div"} py={8} lineHeight={"1.6"}>
            <Text as={"em"} fontWeight={"bold"}>
              {t("learn-more:learn_more.overview.desc1")}
            </Text>
            <br />
            <br />
            <Text as={"em"}>{t("learn-more:learn_more.overview.text")}</Text>
          </Text>
          <H2>{t("learn-more:learn_more.overview.subtitle2")}</H2>
          <Text as={"div"} py={8} lineHeight={"1.6"}>
            <Text as={"h3"} fontWeight={"bold"}>
              {t("learn-more:learn_more.overview.desc2")}
              {/*'Watchman help you:'*/}
            </Text>
            <List spacing={3} mt={4} ml={{ base: 3, "2xl": 6 }}>
              <ListItem justify={"center"}>
                <ListIcon as={MdCheckCircle} color="brand.500" />
                {t("learn-more:learn_more.overview.lists.1")}
                {/*"Detect vulnerabilities related to your operating systems, your company's infrastructure, as well as software;"*/}
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheckCircle} color="brand.500" />
                {t("learn-more:learn_more.overview.lists.2")}
                {/*Manage the resolution of vulnerabilities related to your information system;*/}
              </ListItem>
              <ListItem>
                <ListIcon as={MdCheckCircle} color="brand.500" />
                {t("learn-more:learn_more.overview.lists.3")}
                {/*The establishment of means to protect against future attacks.*/}
              </ListItem>
            </List>
          </Text>
        </Box>
      </Box>
      <Box as={"article"} py={4} px={{ base: 2, "2xl": 8, "3xl": 12 }}>
        <H1
          color={colorMode === "light" ? "inherit" : "brand.600"}
          id={"how-it-works"}
        >
          {t("learn-more:learn_more.howWork.title")}
        </H1>
        <Box
          ml={{ base: "9px", "2xl": "18px" }}
          fontSize={{ base: "md", "2xl": "md", "5xl": "18px" }}
          py={5}
          color={colorMode === "light" ? "gray.700" : "white"}
        >
          <Text as={"div"} lineHeight={"1.6"}>
            <List spacing={3} ml={{ base: 3, "2xl": 6 }}>
              <ListItem pb={2}>
                <ListIcon as={MdCheckCircle} color="brand.500" />
                {t("learn-more:learn_more.howWork.lists.1")}
                {/*Register your business;*/}
              </ListItem>
              <ListItem pb={2}>
                <ListIcon as={MdCheckCircle} color="brand.500" />
                {t("learn-more:learn_more.howWork.lists.2")}
                {/*Start your subscription by accessing the registration form here:*/}
              </ListItem>
              <Alert status="success" variant="left-accent" py={4}>
                <CustomLink
                  to={"/account/choose/"}
                  color={"brand.600"}
                  fontWeight={"bold"}
                >
                  {t("learn-more:learn_more.howWork.lists.3")}
                </CustomLink>
              </Alert>
              <ListItem pb={2}>
                <ListIcon as={MdCheckCircle} color="brand.500" />
                {t("learn-more:learn_more.howWork.lists.4")}
                {/*Add the infrastructures of its information system;*/}
              </ListItem>
              <ListItem pb={2}>
                <ListIcon as={MdCheckCircle} color="brand.500" />
                {t("learn-more:learn_more.howWork.lists.5")}
                {/*{"Add your stacks and their versions to your dashboard's stack list. Once this is done, your stacks are activated automatically."}*/}
              </ListItem>
              <Alert status="info" variant="top-accent" py={4}>
                <AlertIcon />
                {t("learn-more:learn_more.howWork.lists.6")}
                {/*Stacks are the assets (server applications, software and operating system, etc.) of your information system.*/}
              </Alert>
            </List>
          </Text>
        </Box>
      </Box>
      <Box as={"article"} py={4} px={{ base: 2, "2xl": 8, "3xl": 12 }}>
        <H1 id={"add-stacks"} color={colorMode === "light" ? "inherit" : "brand.600"}>{t("learn-more:learn_more.howAdd.title")}</H1>
        <Box
          ml={{ base: "9px", "2xl": "18px" }}
          fontSize={{ base: "md", "2xl": "md", "5xl": "18px" }}
          py={5}
          color={colorMode === "light" ? "gray.700" : "white"}
        >
          <Text as={"div"} lineHeight={"1.6"}>
            <Text as={"h3"} fontWeight={"bold"}>
              {t("learn-more:learn_more.howAdd.ref")}
              {/*{'To add the stacks, you will need to:'}*/}
            </Text>
            <List spacing={3} mt={4} ml={{ base: 3, lg: 6 }}>
              <ListItem mb={2}>
                <ListIcon as={MdCheckCircle} color="brand.500" />
                {t("learn-more:learn_more.howAdd.lists.1")}
                {/*{"Register your company in advance on Watchman;"}*/}
              </ListItem>
              <ListItem mb={2}>
                <ListIcon as={MdCheckCircle} color="brand.500" />
                {t("learn-more:learn_more.howAdd.lists.2")}
                {/*In your stack dashboard, click on the <Text as={"span"} fontWeight={"bold"}>Add stack</Text> button and you will see an Add Stacks form. Fill the form and hit <Text as={"span"} fontWeight={"bold"}>Add</Text> button to add your stack.*/}
              </ListItem>
              <Alert status="info" variant="top-accent" py={4}>
                <AlertIcon />
                {t("learn-more:learn_more.howAdd.text")}
                {/*When there are no stacks, a message notify that. Here are some examples of stacks:*/}
              </Alert>
              <Flex w="full" alignItems="center" justifyContent="center">
                <Table
                  w="full"
                  bg={`${colorMode === "light" ? "white" : "#021b17"}`}
                  display={{
                    base: "table",
                    lg: "table",
                  }}
                  sx={{
                    "@media print": {
                      display: "table",
                    },
                  }}
                >
                  <Thead
                    display={{
                      base: "table-header-group",
                      lg: "table-header-group",
                    }}
                    sx={{
                      "@media print": {
                        display: "table-header-group",
                      },
                    }}
                  >
                    <Tr>
                      <Th fontWeight={"bold"} fontSize={"16px"}>
                        Stacks
                      </Th>
                      <Th fontWeight={"bold"} fontSize={"16px"}>
                        Versions
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody
                    display={{
                      base: "table-row-group",
                      lg: "table-row-group",
                    }}
                    sx={{
                      "@media print": {
                        display: "table-row-group",
                      },
                    }}
                  >
                    <Tr
                      display={{
                        base: "table-row",
                        lg: "table-row",
                      }}
                      sx={{
                        "@media print": {
                          display: "table-row",
                        },
                        gridTemplateColumns:
                          "minmax(0px, 35%) minmax(0px, 65%)",
                        gridGap: "10px",
                      }}
                    >
                      <React.Fragment>
                        <Td
                          sx={{
                            "@media print": {
                              display: "none",
                            },
                            color: colorMode === 'light' ? "blackAlpha.700" : 'white',
                            fontSize: "lg",
                            fontWeight: "semibold",
                            letterSpacing: "wider",
                            fontFamily: "heading",
                          }}
                        >
                          Apache HTTP Server
                        </Td>
                        <Td
                          color={colorMode === 'light' ? "blackAlpha.700" : 'white'}
                          fontSize="lg"
                          fontWeight="semibold"
                        >
                          2.4.52
                        </Td>
                      </React.Fragment>
                    </Tr>
                    <Tr
                      display={{
                        base: "table-row",
                        lg: "table-row",
                      }}
                      sx={{
                        "@media print": {
                          display: "table-row",
                        },
                        gridTemplateColumns:
                          "minmax(0px, 35%) minmax(0px, 65%)",
                        gridGap: "10px",
                      }}
                    >
                      <React.Fragment>
                        <Td
                          sx={{
                            "@media print": {
                              display: "none",
                            },
                            color: colorMode === 'light' ? "blackAlpha.700" : 'white',
                            fontSize: "lg",
                            fontWeight: "semibold",
                            letterSpacing: "wider",
                            fontFamily: "heading",
                          }}
                        >
                          Linux
                        </Td>
                        <Td
                          color={colorMode === 'light' ? "blackAlpha.700" : 'white'}
                          fontSize="lg"
                          fontWeight="semibold"
                        >
                          5.15.11
                        </Td>
                      </React.Fragment>
                    </Tr>
                    <Tr
                      display={{
                        base: "table-row",
                        lg: "table-row",
                      }}
                      sx={{
                        "@media print": {
                          display: "table-row",
                        },
                        gridTemplateColumns:
                          "minmax(0px, 35%) minmax(0px, 65%)",
                        gridGap: "10px",
                      }}
                    >
                      <React.Fragment>
                        <Td
                          sx={{
                            "@media print": {
                              display: "none",
                            },
                            color: colorMode === 'light' ? "blackAlpha.700" : 'white',
                            fontSize: "lg",
                            fontWeight: "semibold",
                            letterSpacing: "wider",
                            fontFamily: "heading",
                          }}
                        >
                          Windows 10
                        </Td>
                        <Td
                          color={colorMode === 'light' ? "blackAlpha.700" : 'white'}
                          fontSize="lg"
                          fontWeight="semibold"
                        >
                          21H2 (10.0.19044.1415)
                        </Td>
                      </React.Fragment>
                    </Tr>
                    <Tr
                      display={{
                        base: "table-row",
                        lg: "table-row",
                      }}
                      sx={{
                        "@media print": {
                          display: "table-row",
                        },
                        gridTemplateColumns:
                          "minmax(0px, 35%) minmax(0px, 65%)",
                        gridGap: "10px",
                      }}
                    >
                      <React.Fragment>
                        <Td
                          sx={{
                            "@media print": {
                              display: "none",
                            },
                            color: colorMode === 'light' ? "blackAlpha.700" : 'white',
                            fontSize: "lg",
                            fontWeight: "semibold",
                            letterSpacing: "wider",
                            fontFamily: "heading",
                          }}
                        >
                          Nginx
                        </Td>
                        <Td
                          color={colorMode === 'light' ? "blackAlpha.700" : 'white'}
                          fontSize="lg"
                          fontWeight="semibold"
                        >
                          1.19
                        </Td>
                      </React.Fragment>
                    </Tr>
                    <Tr
                      display={{
                        base: "table-row",
                        lg: "table-row",
                      }}
                      sx={{
                        "@media print": {
                          display: "table-row",
                        },
                        gridTemplateColumns:
                          "minmax(0px, 35%) minmax(0px, 65%)",
                        gridGap: "10px",
                      }}
                    >
                      <React.Fragment>
                        <Td
                          sx={{
                            "@media print": {
                              display: "none",
                            },
                            color: colorMode === 'light' ? "blackAlpha.700" : 'white',
                            fontSize: "lg",
                            fontWeight: "semibold",
                            letterSpacing: "wider",
                            fontFamily: "heading",
                          }}
                        >
                          Python
                        </Td>
                        <Td
                          color={colorMode === 'light' ? "blackAlpha.700" : 'white'}
                          fontSize="lg"
                          fontWeight="semibold"
                        >
                          3.10
                        </Td>
                      </React.Fragment>
                    </Tr>
                    <Tr
                      display={{
                        base: "table-row",
                        lg: "table-row",
                      }}
                      sx={{
                        "@media print": {
                          display: "table-row",
                        },
                        gridTemplateColumns:
                          "minmax(0px, 35%) minmax(0px, 65%)",
                        gridGap: "10px",
                      }}
                    >
                      <React.Fragment>
                        <Td
                          sx={{
                            "@media print": {
                              display: "none",
                            },
                            color: colorMode === 'light' ? "blackAlpha.700" : 'white',
                            fontSize: "lg",
                            fontWeight: "semibold",
                            letterSpacing: "wider",
                            fontFamily: "heading",
                          }}
                        >
                          Django
                        </Td>
                        <Td
                          color={colorMode === 'light' ? "blackAlpha.700" : 'white'}
                          fontSize="lg"
                          fontWeight="semibold"
                        >
                          4.0
                        </Td>
                      </React.Fragment>
                    </Tr>
                    <Tr
                      display={{
                        base: "table-row",
                        lg: "table-row",
                      }}
                      sx={{
                        "@media print": {
                          display: "table-row",
                        },
                        gridTemplateColumns:
                          "minmax(0px, 35%) minmax(0px, 65%)",
                        gridGap: "10px",
                      }}
                    >
                      <React.Fragment>
                        <Td
                          sx={{
                            "@media print": {
                              display: "none",
                            },
                            color: colorMode === 'light' ? "blackAlpha.700" : 'white',
                            fontSize: "lg",
                            fontWeight: "semibold",
                            letterSpacing: "wider",
                            fontFamily: "heading",
                          }}
                        >
                          Laravel
                        </Td>
                        <Td
                          color={colorMode === 'light' ? "blackAlpha.700" : 'white'}
                          fontSize="lg"
                          fontWeight="semibold"
                        >
                          8.0
                        </Td>
                      </React.Fragment>
                    </Tr>
                    <Tr
                      display={{
                        base: "table-row",
                        lg: "table-row",
                      }}
                      sx={{
                        "@media print": {
                          display: "table-row",
                        },
                        gridTemplateColumns:
                          "minmax(0px, 35%) minmax(0px, 65%)",
                        gridGap: "10px",
                      }}
                    >
                      <React.Fragment>
                        <Td
                          sx={{
                            "@media print": {
                              display: "none",
                            },
                            color: colorMode === 'light' ? "blackAlpha.700" : 'white',
                            fontSize: "sm",
                            fontWeight: "semibold",
                            letterSpacing: "wider",
                            fontFamily: "heading",
                          }}
                        >
                          Chrome
                        </Td>
                        <Td
                          color={colorMode === 'light' ? "blackAlpha.700" : 'white'}
                          fontSize="lg"
                          fontWeight="semibold"
                        >
                          96.0.4664.110
                        </Td>
                      </React.Fragment>
                    </Tr>
                    <Tr
                      display={{
                        base: "table-row",
                        lg: "table-row",
                      }}
                      sx={{
                        "@media print": {
                          display: "table-row",
                        },
                        gridTemplateColumns:
                          "minmax(0px, 35%) minmax(0px, 65%)",
                        gridGap: "10px",
                      }}
                    >
                      <React.Fragment>
                        <Td
                          sx={{
                            "@media print": {
                              display: "none",
                            },
                            color: colorMode === 'light' ? "blackAlpha.700" : 'white',
                            fontSize: "lg",
                            fontWeight: "semibold",
                            letterSpacing: "wider",
                            fontFamily: "heading",
                          }}
                        >
                          Firefox
                        </Td>
                        <Td
                          color={colorMode === 'light' ? "blackAlpha.700" : 'white'}
                          fontSize="sm"
                          fontWeight="semibold"
                        >
                          95.0
                        </Td>
                      </React.Fragment>
                    </Tr>
                  </Tbody>
                </Table>
              </Flex>
            </List>
          </Text>
        </Box>
      </Box>
      <Box as={"article"} py={4} px={{ base: 2, "2xl": 8, "3xl": 12 }}>
        <H1 color={colorMode === "light" ? "inherit" : "brand.600"} id={"next"}>
          {t("learn-more:learn_more.whatNext.title")}
          {/*{"What's next?"}*/}
        </H1>
        <Box
          ml={{ base: "9px", "2xl": "18px" }}
          fontSize={{ base: "md", "2xl": "md", "55xl": "18px" }}
          py={5}
          color={colorMode === "light" ? "gray.700" : "white"}
        >
          <H2>{t("learn-more:learn_more.whatNext.subtitle")}</H2>
          <Text as={"div"} py={4} lineHeight={"1.6"}>
            {t("learn-more:learn_more.whatNext.text")}
            {/*Receive email notifications in case of vulnerability. Your alerts also include links to resources in order to help you better understand the vulnerability and how to fix it.*/}
          </Text>
        </Box>
      </Box>
    </DocShell>
  );
};

const LearnMorePage = () => (
  <Page name="Learn More" path="/docs/learn-more">
    <LearnMore />
  </Page>
);

export default LearnMorePage;


    export async function getStaticProps(ctx) {
        
        
        return {
          
          
          props: {
            
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/docs/learn-more',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  